<template>
  <button
    class="unit-button"
    :class="buttonClasses"
    :type="type"
    :disabled="unavailable"
    :aria-busy="loading ? 'true' : undefined"
    :aria-label="loading ? undefined : ariaLabel || undefined"
    @click="handleClick"
  >
    <span v-if="loading" class="unit-button__loading">
      <UnitSpinner
        :size="spinnerSize"
        :thickness="2"
        track-color="transparent"
        indicator-color="currentColor"
        aria-hidden
      />
      <span
        class="unit-button__loading-label"
        :class="{ 'unit-button__visually-hidden': iconOnly }"
        aria-live="polite"
      >
        {{ loadingLabel }}
      </span>
    </span>
    <template v-else>
      <span v-if="$slots.leading" class="unit-button__slot">
        <slot name="leading" />
      </span>
      <span v-if="$slots.default" class="unit-button__label">
        <slot />
      </span>
      <span v-if="$slots.trailing" class="unit-button__slot">
        <slot name="trailing" />
      </span>
    </template>
  </button>
</template>

<script setup>
import { computed } from "vue";
import UnitSpinner from "@/components/GenericComponents/UnitSpinner.vue";

const props = defineProps({
  variant: {
    type: String,
    default: "primary",
    validator: (value) =>
      ["primary", "secondary", "text", "danger"].includes(value),
  },
  size: {
    type: String,
    default: "default",
    validator: (value) => ["default", "small", "micro"].includes(value),
  },
  type: {
    type: String,
    default: "button",
    validator: (value) => ["button", "submit", "reset"].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  loadingLabel: {
    type: String,
    default: "Loading",
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  iconOnly: {
    type: Boolean,
    default: false,
  },
  ariaLabel: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["click"]);

const unavailable = computed(() => props.disabled || props.loading);
const spinnerSize = computed(() => (props.size === "micro" ? 12 : 16));
const buttonClasses = computed(() => ({
  [`unit-button--${props.variant}`]: true,
  [`unit-button--${props.size}`]: true,
  "unit-button--full-width": props.fullWidth,
  "unit-button--icon-only": props.iconOnly,
  "unit-button--loading": props.loading,
}));

const handleClick = (event) => {
  if (unavailable.value) return;
  emit("click", event);
};
</script>

<style scoped>
.unit-button {
  --color-icon-primary: currentColor;

  display: inline-flex;
  flex: 0 0 auto;
  width: fit-content;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  border: 1px solid transparent;
  border-radius: var(--radius-pill);
  font-family: var(--font-family-ui);
  font-weight: var(--font-weight-medium);
  line-height: 1;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition-property: transform, background-color, border-color, color;
  transition-duration: var(--motion-standard);
  transition-timing-function: var(--motion-easing-standard);
}

.unit-button--default {
  min-height: var(--control-height-default);
  padding: 0 var(--space-6);
  font-size: var(--font-size-body);
}

.unit-button--small {
  height: calc(var(--control-height-compact) * 0.85);
  padding: 0 var(--space-5);
  font-size: var(--font-size-label);
}

.unit-button--micro {
  min-height: var(--control-height-micro);
  gap: var(--space-1);
  padding: 0 var(--space-2);
  font-size: var(--font-size-caption);
}

.unit-button--primary {
  border-color: var(--color-accent-primary);
  background: var(--color-accent-primary);
  color: var(--color-text-on-accent);
}

.unit-button--secondary {
  border-color: var(--color-border-subtle);
  background: transparent;
  color: var(--color-text-primary);
}

.unit-button--secondary:not(:disabled):hover {
  background: var(--color-surface-subtle);
}

.unit-button--text {
  border-color: transparent;
  background: transparent;
  color: var(--color-accent-primary);
}

.unit-button--text:not(:disabled):hover {
  background: var(--color-surface-subtle);
}

.unit-button--danger {
  border-color: var(--color-feedback-danger-border);
  background: var(--color-feedback-danger-surface);
  color: var(--color-text-primary);
}

.unit-button--danger:not(:disabled):hover {
  background: var(--color-feedback-danger-surface-emphasis);
}

.unit-button:not(:disabled):active {
  transform: translateY(var(--motion-pressed-offset));
}

.unit-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
  transform: none;
}

.unit-button--full-width {
  flex: 1 1 100%;
  width: 100%;
}

.unit-button--icon-only {
  width: var(--control-height-default);
  padding: 0;
  aspect-ratio: 1;
}

.unit-button--icon-only.unit-button--small {
  width: calc(var(--control-height-compact) * 0.85);
}

.unit-button--icon-only.unit-button--micro {
  width: var(--control-height-micro);
}

.unit-button__loading,
.unit-button__slot,
.unit-button__label {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  justify-content: center;
}

.unit-button__loading {
  gap: var(--space-2);
}

.unit-button__label,
.unit-button__loading-label {
  overflow-wrap: anywhere;
  white-space: normal;
}

.unit-button__visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
